import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Input } from '@components/core-ui/form-elements/Input';
import { SlugField } from '@components/core-ui/form-elements/SlugField';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { ModalFooter } from '@modals/core-ui/ModalFooter';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { useNameWithSlug } from '@helpers/useNameWithSlug';
import { useSlugAvailability } from '@helpers/useSlugAvailability';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { WorkspacesService } from 'app/API';
import { ApiCallError } from 'app/http/ApiCallError';
import { getApiCallErrorMessage } from 'app/http/getApiCallErrorMessage';
import { createFetchWorkspaceSlugAvailabilityQueryFn } from 'app/queries/workspaces/createFetchWorkspaceSlugAvailabilityQueryFn';
import { workspacesKeys } from 'app/queries/workspaces/workspacesKeys';
import React, { useState } from 'react';
import { Content } from './styles/Content';

type TProps = {
  onCancel: () => void;
};

export const NewWorkspaceModal = ({ onCancel }: TProps) => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const [queryError, setQueryError] = useState('');

  const { updateMe, organization } = useAuthContext();

  const { name, slug, setName, setSlug } = useNameWithSlug();

  const organizationId = organization?.id ?? '';
  const {
    status: slugStatus,
    isAvailable: isSlugAvailable,
    isUnverified: isSlugUnverified,
    retry: retrySlugCheck,
  } = useSlugAvailability({
    slug,
    enabled: !!organizationId,
    checkAvailability: async (value) => {
      const data = await queryClient.fetchQuery({
        queryKey: workspacesKeys.slugAvailability(organizationId, value),
        queryFn: createFetchWorkspaceSlugAvailabilityQueryFn(call),
        staleTime: 0,
      });
      return !!data?.available;
    },
  });

  const createWorkspaceMutation = useMutation({
    mutationFn: (createdInOrganizationId: string) =>
      call(() =>
        WorkspacesService.createWorkspace({
          requestBody: {
            name,
            slug,
            organizationId: createdInOrganizationId,
          },
        }),
      ),
    onMutate: () => {
      setQueryError('');
    },
    onSuccess: async (data) => {
      if (!data) {
        setQueryError('Something went wrong');
        return;
      }
      await updateMe();
      onCancel();
    },
    onError: (error) => {
      setQueryError(error instanceof ApiCallError ? getApiCallErrorMessage(error) : 'Something went wrong');
    },
  });
  const isSubmitting = createWorkspaceMutation.isPending;

  const handleSubmit = () => {
    if (!organization) {
      return;
    }
    createWorkspaceMutation.mutate(organization.id);
  };

  return (
    <ModalWrapper width="400px">
      {isSubmitting ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Create New Workspace</ModalTitle>
      <Content>
        <div className="workspaceSettings-inputWrapper">
          <div className="inputContainer">
            <label htmlFor="title">Workspace Name</label>
            <Input
              label="Name"
              name="title"
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            />
          </div>

          <div className="inputContainer">
            <label htmlFor="slug">URL</label>
            <SlugField label="URL" value={slug} status={slugStatus} onChange={setSlug} onRetry={retrySlugCheck} />
          </div>

          {queryError && <div className="workspaceSettings-errorMessage largeText">{queryError}</div>}
        </div>
        <ModalFooter>
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button
            disabled={isSubmitting || isSlugUnverified || !isSlugAvailable || !(slug && name)}
            onClick={() => handleSubmit()}
          >
            Create
          </Button>
        </ModalFooter>
      </Content>
    </ModalWrapper>
  );
};
